import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    MdInventory, MdSearch, MdFilterList, MdAdd, MdEdit, MdDelete,
    MdWarning, MdClose, MdSave, MdKeyboardArrowDown, MdKeyboardArrowLeft,
    MdKeyboardArrowRight, MdAddShoppingCart, MdStore, MdOutlineInventory2
} from 'react-icons/md';

import { productsAPI } from 'services/api';
import { useAuth } from 'context/AuthContext';

// --- YARDIMCI FONKSİYONLAR ---
const formatCurrency = (value) => {
    return (Number(value) || 0).toLocaleString('tr-TR', {
        style: 'currency',
        currency: 'TRY',
    });
};

const normalizeTr = (str) => {
    return str ? String(str).toLocaleLowerCase('tr-TR') : '';
};

// --- SKELETON LOADERS ---
const ListSkeleton = () => (
    <div className="flex flex-col gap-4 animate-pulse mt-6">
        <div className="h-16 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
                <div key={i} className="h-36 w-full rounded-[20px] bg-gray-100 dark:bg-navy-900/50" />
            ))}
        </div>
    </div>
);

const AdminStockList = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filtreleme & Arama
    const [filterType, setFilterType] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Sayfalama
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 10;

    // Modallar (CoreUI mantığı birebir korundu)
    const [modalConfig, setModalConfig] = useState({ mode: '', data: null, error: null });
    const [formLoading, setFormLoading] = useState(false);

    const { logout } = useAuth();
    const handleAuthError = useCallback(() => logout(), [logout]);

    // --- VERİ ÇEKME ---
    const fetchAllProducts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await productsAPI.getAll();
            if (res.success) {
                const sorted = (res.products || []).sort((a, b) => b.id - a.id);
                setProducts(sorted);
            } else {
                throw new Error('Ürünler yüklenemedi.');
            }
        } catch (err) {
            console.error('Ürün yükleme hatası:', err);
            if (String(err.message).includes('401') || String(err.message).includes('Unauthorized')) {
                handleAuthError();
                return;
            }
            setError(err.message || 'Ürünler yüklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    }, [handleAuthError]);

    useEffect(() => {
        fetchAllProducts();
    }, [fetchAllProducts]);

    // --- LOKAL FİLTRELEME VE ARAMA ---
    const filteredProducts = useMemo(() => {
        let filtered = products;

        if (filterType === 'retail') {
            filtered = filtered.filter(p => p.type === 'retail');
        } else if (filterType === 'internal') {
            filtered = filtered.filter(p => p.type === 'internal');
        } else if (filterType === 'low_stock') {
            filtered = filtered.filter(p => p.quantity <= p.low_stock_threshold);
        }

        if (searchQuery) {
            const q = normalizeTr(searchQuery);
            filtered = filtered.filter(p =>
                normalizeTr(p.name).includes(q) || (p.sku && normalizeTr(p.sku).includes(q))
            );
        }

        return filtered;
    }, [products, filterType, searchQuery]);

    // Sayfalama reset
    useEffect(() => { setCurrentPage(1); }, [filterType, searchQuery]);

    const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
    const currentProducts = filteredProducts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    // --- MODAL İŞLEMLERİ (Açma / Kapama) ---
    const handleOpenModal = (mode, product = null) => {
        setFormLoading(false);
        let modalData = {};

        if (mode === 'add') {
            modalData = {
                name: '', sku: '', description: '', purchase_price: '', selling_price: '',
                tax_rate: 0, initial_quantity: '', low_stock_threshold: 5, type: 'retail',
            };
        } else if (mode === 'edit') {
            modalData = { ...product };
        } else if (mode === 'addStock') {
            modalData = { id: product.id, name: product.name, quantity_added: 1 };
        } else if (mode === 'delete') {
            modalData = product;
        }

        setModalConfig({ mode, data: modalData, error: null });
    };

    const handleCloseModal = () => {
        if (formLoading) return;
        setModalConfig({ mode: '', data: null, error: null });
    };

    const handleModalInputChange = (e) => {
        const { name, value } = e.target;
        setModalConfig(prev => ({
            ...prev,
            data: { ...prev.data, [name]: value },
        }));
    };

    // Ekle / Düzenle Kaydetme İşlemi
    const handleSaveProduct = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        setModalConfig(prev => ({ ...prev, error: null }));
        const { mode, data } = modalConfig;

        try {
            const payload = { ...data };
            if (payload.purchase_price) payload.purchase_price = parseFloat(payload.purchase_price);
            if (payload.selling_price) payload.selling_price = parseFloat(payload.selling_price);
            if (payload.tax_rate) payload.tax_rate = parseFloat(payload.tax_rate);
            if (mode === 'add' && payload.initial_quantity) payload.initial_quantity = parseInt(payload.initial_quantity, 10);
            if (mode === 'edit' && payload.quantity !== undefined) payload.quantity = parseInt(payload.quantity, 10);
            if (payload.low_stock_threshold) payload.low_stock_threshold = parseInt(payload.low_stock_threshold, 10);

            if (mode === 'add') {
                await productsAPI.create(payload);
            } else if (mode === 'edit') {
                await productsAPI.update(payload.id, payload);
            }

            handleCloseModal();
            fetchAllProducts();
        } catch (err) {
            console.error('Ürün kaydetme hatası:', err);
            const errorMsg = err.response?.data?.errors
                ? Object.values(err.response.data.errors).join(', ')
                : (err.message || 'Bir hata oluştu.');
            setModalConfig(prev => ({ ...prev, error: errorMsg }));
        } finally {
            setFormLoading(false);
        }
    };

    // Stok Ekleme (addStock) İşlemi
    const handleAddStock = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        setModalConfig(prev => ({ ...prev, error: null }));
        const { data } = modalConfig;

        const qty = parseInt(data.quantity_added, 10);
        if (!qty || qty < 1) {
            setModalConfig(prev => ({ ...prev, error: 'Eklenecek adet 1 veya daha fazla olmalı.' }));
            setFormLoading(false);
            return;
        }

        try {
            await productsAPI.addStock(data.id, qty);
            handleCloseModal();
            fetchAllProducts();
        } catch (err) {
            setModalConfig(prev => ({ ...prev, error: err.message || 'Stok eklenirken hata oluştu.' }));
        } finally {
            setFormLoading(false);
        }
    };

    // Silme İşlemi (CoreUI'daki birebir silme onayı fonksiyonu)
    const handleDeleteProduct = async () => {
        setFormLoading(true);
        setModalConfig(prev => ({ ...prev, error: null }));
        try {
            await productsAPI.delete(modalConfig.data.id);
            handleCloseModal();
            fetchAllProducts();
        } catch (err) {
            setModalConfig(prev => ({ ...prev, error: err.message || 'Ürün silinirken hata oluştu.' }));
        } finally {
            setFormLoading(false);
        }
    };

    // --- RENDER ---
    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-20 animate-fadeIn">

            {/* 1. ÜST BİLGİ & ANA AKSİYON ALANI */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white p-6 rounded-[20px] shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">
                <div className="flex items-center gap-4 w-full md:w-auto">
                    <div className="flex h-14 w-14 flex-none items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400">
                        <MdInventory className="text-3xl" />
                    </div>
                    <div>
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white">Stok ve Ürün Yönetimi</h4>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Salon içi ürünleri ve satıştaki stokları anlık takip edin.
                        </p>
                    </div>
                </div>
                <button
                    onClick={() => handleOpenModal('add')}
                    className="flex items-center justify-center gap-2 rounded-xl bg-brand-500 px-6 py-3 text-sm font-bold text-white transition-all hover:bg-brand-600 active:scale-95 shadow-lg shadow-brand-500/30 w-full md:w-auto"
                >
                    <MdAdd className="text-xl" /> Yeni Ürün Ekle
                </button>
            </div>

            {error && (
                <div className="p-4 rounded-xl bg-red-50 text-red-600 border border-red-200 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400 text-sm font-bold flex items-center gap-2">
                    <MdWarning className="text-xl" /> {error}
                </div>
            )}

            {/* 2. FİLTRELEME & ARAMA ALANI */}
            <div className="flex flex-col bg-white dark:bg-navy-800 p-5 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10">
                <h5 className="text-base font-bold text-navy-700 dark:text-white flex items-center gap-2 mb-4">
                    <MdFilterList className="text-brand-500" /> Ürün Arama & Filtreleme
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="lg:col-span-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase ml-1 block mb-1">Ürün Adı veya SKU (Barkod)</label>
                        <div className="relative">
                            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Örn: Şampuan, Tarak, Boya..."
                                className="w-full h-11 pl-10 pr-3 rounded-xl border border-gray-200 bg-gray-50 text-sm font-medium text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white transition-all"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase ml-1 block mb-1">Ürün Kullanım Tipi / Durumu</label>
                        <div className="relative">
                            <select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                className="w-full h-11 px-4 appearance-none rounded-xl border border-gray-200 bg-gray-50 text-sm font-bold text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white cursor-pointer transition-all"
                            >
                                <option value="all">Tüm Ürünler</option>
                                <option value="retail">Perakende (Müşteriye Satış)</option>
                                <option value="internal">Salon Kullanımı (Sarf Malzeme)</option>
                                <option value="low_stock">Kritik Stoka Düşenler</option>
                            </select>
                            <MdKeyboardArrowDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-lg" />
                        </div>
                    </div>
                </div>
            </div>

            {/* 3. LİSTELEME ALANI */}
            <div className="bg-white dark:bg-navy-800 p-6 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10">

                {loading ? <ListSkeleton /> : filteredProducts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 opacity-60">
                        <MdOutlineInventory2 className="text-6xl text-gray-300 dark:text-gray-600 mb-4" />
                        <h5 className="text-xl font-bold text-navy-700 dark:text-white">Ürün Bulunamadı</h5>
                        <p className="text-sm font-medium text-gray-500 mt-1 text-center">Filtrelerinize uygun herhangi bir stok verisi yok.</p>
                    </div>
                ) : (
                    <>
                        {/* Masaüstü Tablo Görünümü */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full text-left">
                                <thead>
                                <tr className="border-b border-gray-100 dark:border-white/10 text-[11px] font-bold uppercase tracking-widest text-gray-400">
                                    <th className="py-4 pl-4 w-[35%]">Ürün Bilgisi</th>
                                    <th className="py-4 text-center">Kullanım Tipi</th>
                                    <th className="py-4 text-center">Stok Durumu</th>
                                    <th className="py-4 text-end">Alış (Maliyet)</th>
                                    <th className="py-4 text-end">Satış (Fiyat)</th>
                                    <th className="py-4 pr-4 text-right">İşlemler</th>
                                </tr>
                                </thead>
                                <tbody>
                                {currentProducts.map(product => {
                                    const isLowStock = product.quantity <= product.low_stock_threshold;
                                    return (
                                        <tr key={product.id} className={`border-b border-gray-50 dark:border-white/5 hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors ${isLowStock ? 'bg-red-50/30 dark:bg-red-500/5' : ''}`}>
                                            <td className="py-4 pl-4">
                                                <div className="flex items-center gap-3">
                                                    <div className={`flex h-12 w-12 items-center justify-center rounded-xl font-bold text-lg shadow-sm flex-none ${isLowStock ? 'bg-red-50 text-red-500 dark:bg-red-500/10 dark:text-red-400' : 'bg-gray-50 text-brand-500 dark:bg-navy-900 dark:text-brand-400'}`}>
                                                        <MdOutlineInventory2 />
                                                    </div>
                                                    <div>
                                                        <h6 className="text-sm font-bold text-navy-700 dark:text-white line-clamp-1">{product.name}</h6>
                                                        <p className="text-xs font-medium text-gray-500 mt-0.5">SKU: {product.sku || 'Belirtilmemiş'}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-4 text-center">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${product.type === 'retail' ? 'bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-400' : 'bg-info/10 text-info dark:bg-info/20'}`}>
                          {product.type === 'retail' ? 'Perakende' : 'Salon İçi'}
                        </span>
                                            </td>
                                            <td className="py-4 text-center">
                                                <div className="flex flex-col align-items-center">
                          <span className={`text-lg font-black ${isLowStock ? 'text-red-500' : 'text-navy-700 dark:text-white'}`}>
                            {product.quantity} <span className="text-[10px] font-medium text-gray-400">Adet</span>
                          </span>
                                                    {isLowStock && (
                                                        <span className="text-[10px] font-bold text-red-500 mt-0.5 bg-red-50 dark:bg-red-500/10 px-2 py-0.5 rounded-full inline-flex items-center gap-1 mx-auto">
                              <MdWarning /> Kritik Seviye
                            </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="py-4 text-end">
                                                <span className="text-sm font-bold text-gray-600 dark:text-gray-300">{formatCurrency(product.purchase_price)}</span>
                                            </td>
                                            <td className="py-4 text-end">
                                                <span className="text-sm font-black text-brand-500 dark:text-brand-400">{formatCurrency(product.selling_price)}</span>
                                            </td>
                                            <td className="py-4 pr-4 text-right">
                                                {/* OPACITY KISITLAMASI KALDIRILDI, BUTONLAR HER ZAMAN GÖRÜNÜR */}
                                                <div className="flex items-center justify-end gap-2 transition-opacity">
                                                    <button onClick={() => handleOpenModal('addStock', product)} className="h-9 px-3 flex items-center justify-center gap-1 rounded-lg bg-green-50 text-green-600 hover:bg-green-500 hover:text-white dark:bg-green-500/10 dark:text-green-400 dark:hover:bg-green-500 dark:hover:text-white transition-all text-xs font-bold" title="Stok Ekle">
                                                        <MdAddShoppingCart className="text-sm" /> Ekle
                                                    </button>
                                                    <button onClick={() => handleOpenModal('edit', product)} className="h-9 w-9 flex items-center justify-center rounded-lg bg-gray-50 text-gray-500 hover:text-brand-500 hover:bg-brand-50 dark:bg-navy-900 dark:hover:bg-brand-400/10 dark:hover:text-brand-400 transition-all" title="Düzenle">
                                                        <MdEdit />
                                                    </button>
                                                    <button onClick={() => handleOpenModal('delete', product)} className="h-9 w-9 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-500 hover:text-white dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500 dark:hover:text-white transition-all" title="Sil">
                                                        <MdDelete />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobil Kart Görünümü */}
                        <div className="grid grid-cols-1 md:hidden gap-4">
                            {currentProducts.map(product => {
                                const isLowStock = product.quantity <= product.low_stock_threshold;
                                return (
                                    <div key={product.id} className={`flex flex-col p-4 rounded-[20px] border transition-all ${isLowStock ? 'bg-red-50/30 border-red-100 dark:bg-red-500/5 dark:border-red-500/20' : 'bg-gray-50/50 border-gray-100 dark:bg-navy-900/30 dark:border-white/10'}`}>

                                        <div className="flex justify-between items-start mb-3">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h6 className="text-base font-bold text-navy-700 dark:text-white line-clamp-1">{product.name}</h6>
                                                    {isLowStock && <MdWarning className="text-red-500 text-lg flex-none" />}
                                                </div>
                                                <span className="text-xs font-mono text-gray-500 block mb-2">SKU: {product.sku || '-'}</span>
                                                <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider inline-block ${product.type === 'retail' ? 'bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400' : 'bg-info/10 text-info dark:bg-info/20'}`}>
                          {product.type === 'retail' ? 'Perakende (Satış)' : 'Salon İçi Kullanım'}
                        </span>
                                            </div>
                                            <div className="text-right flex-none pl-2">
                                                <span className="text-xs font-medium text-gray-400 block uppercase">Stok Adedi</span>
                                                <span className={`text-2xl font-black ${isLowStock ? 'text-red-500' : 'text-navy-700 dark:text-white'}`}>{product.quantity}</span>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-2 mb-4">
                                            <div className="p-2 rounded-xl bg-white dark:bg-navy-800 border border-gray-100 dark:border-white/5">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Alış (Maliyet)</span>
                                                <span className="text-sm font-bold text-gray-600 dark:text-gray-300">{formatCurrency(product.purchase_price)}</span>
                                            </div>
                                            <div className="p-2 rounded-xl bg-white dark:bg-navy-800 border border-gray-100 dark:border-white/5">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Satış (Fiyat)</span>
                                                <span className="text-sm font-black text-brand-500 dark:text-brand-400">{formatCurrency(product.selling_price)}</span>
                                            </div>
                                        </div>

                                        <div className="flex justify-between gap-2 border-t border-gray-100 dark:border-white/10 pt-3">
                                            <button onClick={() => handleOpenModal('addStock', product)} className="flex-1 h-9 px-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-500 hover:text-white dark:bg-green-500/10 dark:text-green-400 dark:hover:bg-green-500 dark:hover:text-white transition-all text-xs font-bold flex items-center justify-center gap-1">
                                                <MdAddShoppingCart className="text-sm" /> Stok Ekle
                                            </button>
                                            <button onClick={() => handleOpenModal('edit', product)} className="h-9 w-9 rounded-lg bg-white dark:bg-navy-800 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 flex items-center justify-center flex-none"><MdEdit /></button>
                                            <button onClick={() => handleOpenModal('delete', product)} className="h-9 w-9 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-500 dark:text-red-400 flex items-center justify-center flex-none"><MdDelete /></button>
                                        </div>

                                    </div>
                                );
                            })}
                        </div>

                        {/* SAYFALAMA */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100 dark:border-white/10">
                                <p className="text-sm font-medium text-gray-500">
                                    <span className="font-bold text-navy-700 dark:text-white">{filteredProducts.length}</span> kayıttan <span className="font-bold text-navy-700 dark:text-white">{(currentPage-1)*PAGE_SIZE + 1}-{Math.min(currentPage*PAGE_SIZE, filteredProducts.length)}</span> gösteriliyor.
                                </p>
                                <div className="flex gap-2">
                                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all">
                                        <MdKeyboardArrowLeft className="text-xl" />
                                    </button>
                                    <div className="flex h-9 min-w-[36px] items-center justify-center rounded-lg bg-brand-500 text-white text-sm font-bold shadow-md px-2">
                                        {currentPage}
                                    </div>
                                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all">
                                        <MdKeyboardArrowRight className="text-xl" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* 4. MODALLAR (ŞİMDİ SORUNSUZ AÇILIR) */}

            {/* 4.1 EKLE/DÜZENLE MODALI */}
            {modalConfig.mode === 'add' || modalConfig.mode === 'edit' ? (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-navy-900/40 backdrop-blur-sm p-4 transition-all animate-fadeIn">
                    <div className="relative w-full max-w-3xl bg-white dark:bg-navy-800 rounded-[24px] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

                        <div className="flex-none flex items-center justify-between p-6 border-b border-gray-100 dark:border-white/10">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400">
                                    {modalConfig.mode === 'add' ? <MdAdd className="text-xl" /> : <MdEdit className="text-xl" />}
                                </div>
                                <h3 className="text-xl font-bold text-navy-700 dark:text-white">
                                    {modalConfig.mode === 'add' ? 'Yeni Ürün Ekle' : 'Ürünü Düzenle'}
                                </h3>
                            </div>
                            <button onClick={handleCloseModal} className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-navy-700 rounded-full transition-all">
                                <MdClose className="text-xl" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto custom-modal-scroll p-6">
                            {modalConfig.error && (
                                <div className="mb-6 flex items-center gap-3 rounded-xl bg-red-50 p-4 text-red-600 border border-red-100 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
                                    <MdWarning className="text-xl flex-none" />
                                    <span className="text-sm font-bold">{modalConfig.error}</span>
                                </div>
                            )}

                            <form id="productForm" onSubmit={handleSaveProduct} className="flex flex-col gap-5">

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 block">Ürün Adı *</label>
                                        <input required name="name" value={modalConfig.data.name || ''} onChange={handleModalInputChange} placeholder="Örn: X Marka Şampuan" className="flex h-12 w-full items-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm font-medium text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400 transition-all" />
                                    </div>
                                    <div>
                                        <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 block">SKU / Barkod Kodu</label>
                                        <input name="sku" value={modalConfig.data.sku || ''} onChange={handleModalInputChange} placeholder="Stok Kodu" className="flex h-12 w-full items-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm font-medium text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400 transition-all" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 block">Kullanım Tipi *</label>
                                        <div className="relative">
                                            <select name="type" value={modalConfig.data.type || 'retail'} onChange={handleModalInputChange} className="w-full h-12 appearance-none rounded-xl border border-gray-200 bg-white/0 px-3 text-sm font-bold text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:bg-navy-900 transition-all cursor-pointer">
                                                <option value="retail">Perakende (Müşteriye Satış)</option>
                                                <option value="internal">Salon Kullanımı (Sarf Malzeme)</option>
                                            </select>
                                            <MdKeyboardArrowDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 block">KDV / Vergi Oranı (%)*</label>
                                        <div className="relative">
                                            <input type="number" step="0.01" min="0" name="tax_rate" value={modalConfig.data.tax_rate ?? 0} onChange={handleModalInputChange} className="flex h-12 w-full items-center rounded-xl border border-gray-200 bg-white/0 p-3 pr-10 text-sm font-medium text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400 transition-all" />
                                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">%</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 block">Alış Fiyatı (Maliyet) *</label>
                                        <div className="relative">
                                            <input required type="number" step="0.01" min="0" name="purchase_price" value={modalConfig.data.purchase_price ?? ''} onChange={handleModalInputChange} placeholder="0.00" className="flex h-12 w-full items-center rounded-xl border border-gray-200 bg-white/0 p-3 pr-10 text-sm font-medium text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400 transition-all" />
                                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">₺</span>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 block">Satış Fiyatı *</label>
                                        <div className="relative">
                                            <input required type="number" step="0.01" min="0" name="selling_price" value={modalConfig.data.selling_price ?? ''} onChange={handleModalInputChange} placeholder="0.00" className="flex h-12 w-full items-center rounded-xl border border-gray-200 bg-white/0 p-3 pr-10 text-sm font-medium text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400 transition-all" />
                                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">₺</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    {modalConfig.mode === 'add' ? (
                                        <div>
                                            <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 block">Başlangıç Stok Adedi *</label>
                                            <input required type="number" step="1" min="0" name="initial_quantity" value={modalConfig.data.initial_quantity ?? ''} onChange={handleModalInputChange} className="flex h-12 w-full items-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm font-medium text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400 transition-all" />
                                        </div>
                                    ) : (
                                        <div>
                                            <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 block text-amber-500">Mevcut Stok Adedi (Manuel Güncelleme)</label>
                                            <input required type="number" step="1" min="0" name="quantity" value={modalConfig.data.quantity ?? ''} onChange={handleModalInputChange} className="flex h-12 w-full items-center rounded-xl border border-amber-200 bg-amber-50/30 p-3 text-sm font-bold text-amber-700 outline-none focus:border-amber-500 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400 transition-all" />
                                        </div>
                                    )}
                                    <div>
                                        <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 block">Kritik Stok Uyarı Sınırı *</label>
                                        <input required type="number" step="1" min="0" name="low_stock_threshold" value={modalConfig.data.low_stock_threshold ?? 5} onChange={handleModalInputChange} className="flex h-12 w-full items-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm font-medium text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400 transition-all" />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 block">Açıklama (Opsiyonel)</label>
                                    <textarea name="description" value={modalConfig.data.description || ''} onChange={handleModalInputChange} rows="3" className="flex w-full items-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm font-medium text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400 transition-all resize-none" placeholder="Ürün hakkında kısa notlar..." />
                                </div>

                            </form>
                        </div>

                        <div className="flex-none flex items-center justify-end gap-3 p-6 border-t border-gray-100 dark:border-white/10 bg-gray-50 dark:bg-navy-800/50">
                            <button onClick={handleCloseModal} className="px-6 h-11 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-navy-700 transition-all">İptal</button>
                            <button type="submit" form="productForm" disabled={formLoading} className="flex items-center justify-center gap-2 h-11 px-8 rounded-xl bg-brand-500 text-sm font-bold text-white shadow-lg shadow-brand-500/30 hover:bg-brand-600 disabled:opacity-60 transition-all active:scale-95">
                                {formLoading ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><MdSave className="text-lg" /> {modalConfig.mode === 'add' ? 'Ekle' : 'Güncelle'}</>}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* 4.2 STOKA EKLE (ADD STOCK) MODALI */}
            {modalConfig.mode === 'addStock' ? (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-navy-900/40 backdrop-blur-sm p-4 transition-all animate-fadeIn">
                    <div className="relative w-full max-w-sm bg-white dark:bg-navy-800 rounded-[24px] shadow-2xl p-6">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="h-12 w-12 rounded-full bg-green-50 text-green-500 flex items-center justify-center dark:bg-green-500/10 dark:text-green-400">
                                <MdAddShoppingCart className="text-2xl" />
                            </div>
                            <h3 className="text-xl font-bold text-navy-700 dark:text-white">Stoğa Ürün Ekle</h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                            <strong>{modalConfig.data?.name}</strong> isimli ürüne yeni stok girişi yapıyorsunuz.
                        </p>
                        {modalConfig.error && (
                            <div className="mb-4 p-3 rounded-xl bg-red-50 text-sm font-bold text-red-600 dark:bg-red-500/10 dark:text-red-400">{modalConfig.error}</div>
                        )}

                        <form onSubmit={handleAddStock}>
                            <div className="mb-6">
                                <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 block">Eklenecek Adet *</label>
                                <input required autoFocus type="number" step="1" min="1" name="quantity_added" value={modalConfig.data?.quantity_added ?? 1} onChange={handleModalInputChange} className="flex h-12 w-full items-center rounded-xl border border-green-200 bg-green-50/50 p-3 text-lg font-black text-green-600 outline-none focus:border-green-500 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-400 transition-all text-center" />
                            </div>

                            <div className="flex justify-end gap-3">
                                <button type="button" onClick={handleCloseModal} className="px-6 h-11 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-700 transition-all">İptal</button>
                                <button type="submit" disabled={formLoading} className="flex items-center justify-center h-11 px-6 rounded-xl bg-green-500 text-sm font-bold text-white shadow-lg shadow-green-500/30 hover:bg-green-600 disabled:opacity-60 transition-all active:scale-95">
                                    {formLoading ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Stoğa Ekle'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}

            {/* 4.3 SİLME MODALI */}
            {modalConfig.mode === 'delete' ? (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-navy-900/40 backdrop-blur-sm p-4 transition-all animate-fadeIn">
                    <div className="relative w-full max-w-sm bg-white dark:bg-navy-800 rounded-[24px] shadow-2xl p-6">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="h-12 w-12 rounded-full bg-red-50 text-red-500 flex items-center justify-center dark:bg-red-500/10 dark:text-red-400">
                                <MdWarning className="text-2xl" />
                            </div>
                            <h3 className="text-xl font-bold text-navy-700 dark:text-white">Ürünü Sil</h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                            <strong>"{modalConfig.data?.name}"</strong> ürününü kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve geçmiş raporları etkileyebilir.
                        </p>
                        {modalConfig.error && (
                            <div className="mb-4 p-3 rounded-xl bg-red-50 text-sm font-bold text-red-600 dark:bg-red-500/10 dark:text-red-400">{modalConfig.error}</div>
                        )}
                        <div className="flex justify-end gap-3">
                            <button onClick={handleCloseModal} className="px-6 h-11 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-700 transition-all">İptal</button>
                            <button onClick={handleDeleteProduct} disabled={formLoading} className="flex items-center justify-center h-11 px-8 rounded-xl bg-red-500 text-sm font-bold text-white shadow-lg shadow-red-500/30 hover:bg-red-600 disabled:opacity-60 transition-all active:scale-95">
                                {formLoading ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Evet, Sil'}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* STİLLER */}
            <style jsx="true">{`
                .custom-modal-scroll::-webkit-scrollbar { width: 6px; }
                .custom-modal-scroll::-webkit-scrollbar-track { background: transparent; }
                .custom-modal-scroll::-webkit-scrollbar-thumb { background: rgba(163, 174, 208, 0.3); border-radius: 10px; }
                .dark .custom-modal-scroll::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); }
            `}</style>
        </div>
    );
};

export default AdminStockList;